import type { CmmdUserForgeAutomationRule, ModelSelection } from "@cmmd-forge/contracts";

import type { AutomationTemplateDefinition } from "./automationTemplates";
import type { LocalAutomation, LocalAutomationScheduleKind } from "./localAutomationClient";

export type AutomationRunLocation = "cloud" | "local";
export type AutomationScheduleKind = LocalAutomationScheduleKind | "custom";

const AUTOMATION_PROVIDER_KINDS = new Set<ModelSelection["provider"]>([
  "codex",
  "claudeAgent",
  "ollama",
  "cmmd",
  "apex",
  "cursor",
  "opencode",
]);

function automationModelSelection(
  selection: CmmdUserForgeAutomationRule["modelSelection"],
): ModelSelection | null {
  if (
    !selection ||
    !AUTOMATION_PROVIDER_KINDS.has(selection.provider as ModelSelection["provider"])
  ) {
    return null;
  }
  return { provider: selection.provider, model: selection.model } as ModelSelection;
}

export interface AutomationProjectOption {
  readonly id: string;
  readonly name: string;
  readonly githubRepo: string | null;
}

export interface AutomationDraft {
  readonly name: string;
  readonly modelSelection: ModelSelection | null;
  readonly presetKey: string;
  readonly prompt: string;
  readonly runLocation: AutomationRunLocation;
  readonly scheduleDayOfWeek: number;
  readonly scheduleKind: AutomationScheduleKind;
  readonly scheduleTime: string;
  readonly preservedCronExpression: string | null;
  readonly preservedScheduleDescription: string | null;
  readonly selectedProjectId: string;
  readonly requirePrReview: boolean;
  readonly timezone: string;
}

function currentTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

export function blankAutomationDraft(
  projects: readonly AutomationProjectOption[],
): AutomationDraft {
  return {
    name: "",
    modelSelection: null,
    presetKey: "custom",
    prompt: "",
    runLocation: "cloud",
    scheduleDayOfWeek: 1,
    scheduleKind: "manual",
    scheduleTime: "09:00",
    preservedCronExpression: null,
    preservedScheduleDescription: null,
    selectedProjectId: projects[0]?.id ?? "",
    requirePrReview: true,
    timezone: currentTimezone(),
  };
}

export function draftFromTemplate(
  template: AutomationTemplateDefinition,
  projects: readonly AutomationProjectOption[],
): AutomationDraft {
  return {
    ...blankAutomationDraft(projects),
    name: template.name,
    presetKey: template.key,
    prompt: template.prompt,
    scheduleDayOfWeek: template.schedule.dayOfWeek ?? 1,
    scheduleKind: template.schedule.kind,
    scheduleTime: template.schedule.time ?? "09:00",
  };
}

function cronTimeParts(cronExpression: string | null): {
  readonly minute: number;
  readonly hour: number;
  readonly dayOfWeek: number | null;
} | null {
  if (!cronExpression) return null;
  const match = cronExpression.trim().match(/^(\d+)\s+(\d+)\s+\*\s+\*\s+(\*|[0-6])$/);
  if (!match) return null;
  const minute = Number(match[1]);
  const hour = Number(match[2]);
  const dayOfWeek = match[3] === "*" ? null : Number(match[3]);
  return { minute, hour, dayOfWeek };
}

function scheduleFromCron(
  cronExpression: string | null,
): Pick<
  AutomationDraft,
  | "preservedCronExpression"
  | "preservedScheduleDescription"
  | "scheduleDayOfWeek"
  | "scheduleKind"
  | "scheduleTime"
> {
  if (cronExpression?.trim() === "* * * * *") {
    return {
      preservedCronExpression: null,
      preservedScheduleDescription: null,
      scheduleDayOfWeek: 1,
      scheduleKind: "every_minute",
      scheduleTime: "09:00",
    };
  }
  const parts = cronTimeParts(cronExpression);
  if (!parts) {
    return cronExpression
      ? {
          preservedCronExpression: cronExpression,
          preservedScheduleDescription: null,
          scheduleDayOfWeek: 1,
          scheduleKind: "custom",
          scheduleTime: "09:00",
        }
      : {
          preservedCronExpression: null,
          preservedScheduleDescription: null,
          scheduleDayOfWeek: 1,
          scheduleKind: "manual",
          scheduleTime: "09:00",
        };
  }
  const scheduleTime = `${String(parts.hour).padStart(2, "0")}:${String(parts.minute).padStart(2, "0")}`;
  return parts.dayOfWeek === null
    ? {
        preservedCronExpression: null,
        preservedScheduleDescription: null,
        scheduleDayOfWeek: 1,
        scheduleKind: "daily",
        scheduleTime,
      }
    : {
        preservedCronExpression: null,
        preservedScheduleDescription: null,
        scheduleDayOfWeek: parts.dayOfWeek,
        scheduleKind: "weekly",
        scheduleTime,
      };
}

export function draftFromCloudRule(rule: CmmdUserForgeAutomationRule): AutomationDraft {
  const schedule = scheduleFromCron(rule.cronExpression);
  return {
    ...schedule,
    name: rule.name,
    modelSelection: automationModelSelection(rule.modelSelection),
    presetKey: rule.presetKey ?? "custom",
    prompt: rule.prompt,
    preservedScheduleDescription:
      schedule.scheduleKind === "custom" ? rule.scheduleDescription : null,
    runLocation: "cloud",
    selectedProjectId: String(rule.projectId),
    requirePrReview: rule.requirePrReview,
    timezone: rule.timezone,
  };
}

export function draftFromLocalAutomation(automation: LocalAutomation): AutomationDraft {
  return {
    name: automation.name,
    modelSelection: null,
    presetKey: "custom",
    prompt: automation.prompt,
    runLocation: "local",
    scheduleDayOfWeek: automation.scheduleDayOfWeek ?? 1,
    scheduleKind: automation.scheduleKind,
    scheduleTime: automation.scheduleTime ?? "09:00",
    preservedCronExpression: null,
    preservedScheduleDescription: null,
    selectedProjectId: "",
    requirePrReview: true,
    timezone: automation.timezone,
  };
}

export function cronFromDraft(draft: AutomationDraft): string | null {
  if (draft.scheduleKind === "custom") return draft.preservedCronExpression;
  if (draft.scheduleKind === "manual") return null;
  if (draft.scheduleKind === "every_minute") return "* * * * *";
  const [hour = "9", minute = "0"] = draft.scheduleTime.split(":");
  return draft.scheduleKind === "daily"
    ? `${Number(minute)} ${Number(hour)} * * *`
    : `${Number(minute)} ${Number(hour)} * * ${draft.scheduleDayOfWeek}`;
}

export function scheduleDescriptionFromDraft(draft: AutomationDraft): string | null {
  if (draft.scheduleKind === "custom") {
    return draft.preservedScheduleDescription;
  }
  if (draft.scheduleKind === "manual") return "Manual";
  if (draft.scheduleKind === "every_minute") return "Every minute";
  if (draft.scheduleKind === "daily") return `Daily at ${draft.scheduleTime}`;
  const day =
    ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][
      draft.scheduleDayOfWeek
    ] ?? "Monday";
  return `${day} at ${draft.scheduleTime}`;
}
